import type { Credentials, S3Bucket } from "modules/objectStorage/types";
export type Load = {
  cpu_value: number;
  memory_value: number;
  time_stamp: number;
};

export type HWLoad = {
  load: Load[] | null;
  duration_seconds: number;
  range_minutes: number;
};

// High Availability config (mirrors spec.highAvailability). Used both as a
// response echo and as a create/update request payload.
export type DatabaseHA = {
  enabled: boolean;
  failover_delay_seconds?: number;
};

export type PoolerMode = "transaction" | "session";

export type PoolerUpdate = { enabled: boolean; mode?: PoolerMode };

export type DatabaseImageFlavor = "bitnami" | "upstream";

export type DatabaseAccountManagement = "Operator" | "Tenant";

// PgBouncer on its own endpoint. In transaction mode a client keeps its connection
// through a switchover or failover.
export type DatabasePooler = {
  enabled: boolean;
  mode: PoolerMode;
  ready: boolean;
  host?: string;
  port?: number;
  message?: string;
};

// Version-upgrade progress (from status.upgrade). Present only while an
// upgrade is/was in flight.
export type DatabaseUpgrade = {
  phase?: string;
  from_version?: string;
  to_version?: string;
  message?: string;
};

// One member of the HA group (status.replicaStatus.members).
export type DatabaseHAMember = {
  role?: string;
  healthy: boolean;
};

// HA/failover group health (from status.replicaStatus).
export type DatabaseReplicaStatus = {
  phase?: string;
  ready_replicas?: number;
  last_failover_time?: number;
  members?: DatabaseHAMember[];
};

export type InternalLoadBalancer = {
  name: string;
  subnet_id: string;
  private_static_ip?: string;
  ip_acl?: string[];
};

export type DatabaseLoadBalancerPooler = {
  host?: string;
  port: number;
  ready: boolean;
  message?: string;
};

export type DatabaseLoadBalancer = InternalLoadBalancer & {
  host?: string;
  port: number;
  ready: boolean;
  message?: string;
  // The pooler's endpoint on the same network and allow-list, while it is on.
  pooler?: DatabaseLoadBalancerPooler;
};

export type Database = {
  id: string;
  project_id: string;
  name: string;
  user: string;
  password: string;
  ip_acl: string[];
  connection_string: string;
  hw_load: HWLoad;
  port: number;
  version: string;
  message?: string;
  region: string;
  region_instance: string;
  cpu: number;
  memory: number;
  disk: number;
  volume_capacity_used: number;
  mode: DATABASE_REPLICA_STATUSES;
  service_name: DATABASES;
  service_status: DATABASE_STATUSES;
  custom_parameters: DatabaseCustomOptions[];
  subnet_id: string;
  load_balancer_message?: string;
  public_load_balancer_enabled?: boolean;
  public_load_balancer?: DatabaseLoadBalancer;
  internal_load_balancers?: DatabaseLoadBalancer[];
  creation_time?: number;
  org_id: string;
  // The version actually running (may lag `version` during an upgrade).
  observed_version?: string;
  // Versions the instance may move to; the update endpoint refuses anything else.
  available_versions?: string[];
  ready_replicas?: number;
  high_availability?: DatabaseHA;
  pooler?: DatabasePooler;
  upgrade?: DatabaseUpgrade;
  replica_status?: DatabaseReplicaStatus;
  migration?: DatabaseMigration;
  // Image family the instance runs on; absent until the operator has recorded it.
  image_flavor?: DatabaseImageFlavor;
  // "Tenant": databases, users and grants are managed inside the engine; the lists show
  // its live inventory and every account write is refused. Absent on an older API.
  account_management?: DatabaseAccountManagement;
  accounts_editable?: boolean;
  accounts_read_only_reason?: string;
  // "user/database: reason" entries the operator will not apply.
  rejected_permissions?: string[];
  // "database/extension: reason" entries not installed.
  rejected_extensions?: string[];
  // "database/extension: reason" entries that install once the database restarts: not refused.
  pending_extensions?: string[];
  // "database/key: reason" entries not applied.
  rejected_settings?: string[];
  // "database: reason" entries created with the server default charset/collation.
  rejected_encodings?: string[];
  retained_extensions?: string[];
};

// An operator-driven migration of a chart-era or Bitnami database; `required` with
// phase Pending means it restarts the database once and waits for approval.
export enum DATABASE_MIGRATION_PHASES {
  PENDING = "Pending",
  RUNNING = "Running",
  VERIFYING = "Verifying",
  REINDEXING = "Reindexing",
  COMPLETED = "Completed",
  FAILED = "Failed",
  // Would lose something (e.g. an extension the target image lacks); the message says what.
  BLOCKED = "Blocked"
}

export type DatabaseMigration = {
  kind?: string;
  phase?: DATABASE_MIGRATION_PHASES;
  message?: string;
  required: boolean;
};

export type DatabaseCustomOptions = {
  title?: string;
  unit?: string;
  description?: string;
  restart?: string;
  apply_mode?: "restart" | "live";
  allow_empty?: string;
  has_custom_value?: string;
  special_values?: string;
  risk?: string;
  editable?: string;
  values?: string;
  effective_value?: string;
  state?: "default" | "pending" | "applied" | "rejected" | "unknown";
  name: string;
  type: string;
  min_value: string;
  max_value: string;
  default_value: string;
  custom_value?: string;
  rejected_reason?: string;
  pending_reason?: string;
};

export type TableDatabase = Database & {
  connectionString: string;
  versionString: string;
  cpuString: string;
  memoryString: string;
  diskString: string;
  volumeCapacity: string;
  serviceName: string;
  ageString: string;
  ipACL: string[];
};

export type DatabaseResetPassword = {
  id: string;
  project_id: string;
  user: string;
  password?: string;
};

export type TableReplicaDatabase = TableDatabase;

export type TableCronStatus = {
  id: string;
  status: string;
  date: number;
  dateString: string;
};

export type DatabaseBackupStatus = {
  backup_lastrun_date?: number;
  // Outcome of the last completed backup ("Success" / "Failed"); backup_status is the operation state.
  backup_lastrun_result?: string;
  backup_lastrun_message?: string;
  backup_status?: DATABASE_BACKUP_STATUSES;
  cron_config?: string;
  cron_exist: boolean;
  cron_retention?: string;
  cron_timezone?: string;
  cron_next_run?: number;
  cron_runs?: {
    id: string;
    date: number;
    status: DATABASE_BACKUP_STATUSES;
  }[];
  link_exist: boolean;
  restore_lastrun_date?: number;
  restore_lastrun_result?: string;
  restore_lastrun_message?: string;
  restore_status?: DATABASE_RESTORE_STATUSES;
  // Where the operator's restore machine is; restore_status is the operation state.
  restore_phase?: DATABASE_RESTORE_PHASES;
  restore_message?: string;
  // A pre-restore copy exists, also after a restore that succeeded.
  rollback_available?: boolean;
  rollback_taken_date?: number;
  s3endpoint?: string;
  s3bucket?: string;
  s3key?: string;
};

export type DatabaseBackupFile = {
  id: string;
  etag: string;
  file_name: string;
  file_size: number;
  // Read out of the object name; empty for an object the operator did not write.
  database?: string;
  // The run timestamp, YYYYmmdd-HHMMSS. Objects sharing one are one backup SET.
  taken?: string;
  // Taken automatically before a restore so it can be undone; not a backup anyone asked for.
  safety_copy?: boolean;
  // An older-layout object nothing ties to this database: listed, not restorable.
  unattributed?: boolean;
};

// One backup run: every object it wrote, across every database. Grouped by the API.
export type BackupSet = {
  id: string;
  taken: string;
  files: DatabaseBackupFile[];
  databases: string[];
  total_size: number;
  safety_copy?: boolean;
};

// GET /backups?group=set: the flat listing and the runs derived from it.
export type BackupListGrouped = {
  files: DatabaseBackupFile[];
  sets: BackupSet[];
};

export type TableBackupSet = {
  id: string;
  taken: string;
  databases: string;
  fileCount: number;
  totalSize: string;
  safety: string;
};

export type TableDatabaseBackupFiles = DatabaseBackupFile & {
  fileSize: string;
  note: string;
};

export type TableDatabaseCustomOptions = DatabaseCustomOptions & {
  id: string;
  nameString: string;
};

export type CronBackupParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    cron: string;
    retention: string;
    timezone: string;
    // Same meaning as on a manual backup: omit for every database.
    databases?: string[];
  };
};

export type CronBackupResponse = Database;

export type CronDeleteParams = GetDatabaseParams;

export type CronDeleteResponse = Database;

export type RestoreDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    database: string;
    file: string;
    // Restore several databases in one request, each object into its own target.
    // When present the API ignores `database` and `file`.
    items?: { file: string; database?: string }[];
    // Copy the current data first so the restore can be undone. Defaults to true on
    // the API side; sent explicitly only to turn it OFF, which is the deliberate act.
    safety_backup?: boolean;
  };
};

export type RollbackRestoreParams = GetDatabaseParams;

export type RollbackRestoreResponse = Database;

export type RestoreDatabaseResponse = Database;

export type GetDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  pollingId?: string;
};

export type CreateBackupDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    timezone: string;
    // Omit for every database. A selection is only offered where the engine's backup
    // is per database -- see supportsPerDatabaseBackups.
    databases?: string[];
  };
};

export type CreateBackupDatabaseResponse = Database;

export type GetReplicaDatabaseParams = GetDatabaseParams;

export type GetDatabaseCustomOptionsParams = GetDatabaseParams;

export type GetReplicaDatabasesResponse = Database[];

export type GetDatabaseResponse = Database;

export type DatabaseMetrics = {
  id: string;
  volume_capacity_used: number;
  hw_load: HWLoad;
  message?: string;
};

export type GetDatabaseMetricsParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  duration?: number; // resolution in seconds (5-3600)
  range?: number; // window in minutes (1-168)
  pollingId?: string;
};

export type GetDatabaseMetricsResponse = DatabaseMetrics;

export type GetDatabaseCustomOptionsResponse = Database;

export type UpdateDatabaseCustomOptionsResponse = { message: string };

export type GetDatabaseBackupStatusResponse = DatabaseBackupStatus;

export type GetDatabaseBackupFilesResponse = BackupListGrouped;

export type GetDatabasesParams = {
  orgId: string;
  projId: string;
  pollingId?: string;
};

export type GetDatabasesResponse = Database[];

export type CreateDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  data: {
    name: string;
    user?: string;
    ip_acl?: string[];
    cpu: number;
    memory: number;
    disk: number;
    port?: number;
    version?: string;
    subnet_id?: string;
    private_static_ip?: string;
    high_availability?: DatabaseHA;
    extensions?: string[];
  };
};

export type UpdateDatabaseCustomOptionsParams = {
  unset?: string;
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    name: string;
    value: string;
  }[];
};

export type CreateDatabaseResponse = Database;

export type DbaasQuotas = {
  max_value_of_instance: number;
  max_value_of_cpu: number;
  max_value_of_memory: number;
  max_value_of_disk: number;
  current_value_of_instance: number;
  current_value_of_cpu: number;
  current_value_of_memory: number;
  current_value_of_disk: number;
};

export type GetDbaasQuotasParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  pollingId?: string;
};

export type GetDbaasQuotasResponse = DbaasQuotas;

export type CreateReplicaDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    project_id: string;
  };
};

export type CreateReplicaDatabaseResponse = Database;

export type UpdateDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    ip_acl?: string[];
    cpu?: number;
    memory?: number;
    // Grow only: the API rejects a value below the current size, because volume
    // expansion is one-way in Kubernetes.
    disk?: number;
    port?: number;
    version?: string;
    high_availability?: DatabaseHA;
    pooler?: PoolerUpdate;
    public_load_balancer_enabled?: boolean;
    internal_load_balancers?: InternalLoadBalancer[];
  };
};

export type UpdateDatabaseResponse = Database;

export type DeleteDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
};

export type DeleteDatabaseResponse = Database;

export type GetManagementUrlParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
};

export type GetManagementUrlResponse = DBManagementUrl;

export type DBManagementUrl = string;

export type DBServiceParams = {
  service_name: string;
  cpu: string[];
  disk_max: string;
  disk_min: string;
  memory: string[];
  versions: string[];
  extensions?: ExtensionInfo[];
};

export type ExtensionSettingType =
  "integer" | "real" | "bool" | "enum" | "size" | "timezone" | "set" | "flags";

export type ExtensionSetting = {
  allowEmpty?: boolean;
  specialValues?: string[];
  unit?: string;
  name: string;
  // Human-readable name for forms; the parameter name is shown when absent.
  title?: string;
  type: ExtensionSettingType;
  min?: string;
  max?: string;
  default?: string;
  values?: string[];
  description?: string;
};

export type ExtensionInfo = {
  name: string;
  description?: string;
  restart?: boolean;
  // PostgreSQL majors it runs on; absent = unbounded.
  min_major?: number;
  max_major?: number;
  official_image_only?: boolean;
  settings?: ExtensionSetting[];
};

export type GetDBServiceParamsParams = {
  serviceName: string;
  pollingId?: string;
};

export type GetDBServiceParamsResponse = DBServiceParams;

export type GetDBServicesParamsParams = {
  pollingId?: string;
};

export type GetDBServicesParamsResponse = DBServiceParams[];

export enum CHANGE_STATUS_TYPES {
  START = "start",
  STOP = "stop"
}

export type ChangeDatabaseStatusParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  type: CHANGE_STATUS_TYPES;
};

export type ChangeDatabaseStatusResponse = {
  message: string;
};

export type CreateDatabaseUserResponse = Database;

export type CreateDatabaseChildResponse = {
  message: string;
};

export type ResetDatabasePasswordResponse = DatabaseResetPassword;

export type ResetDatabasePasswordParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
};

export type S3LinkDatabaseParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    endpoint: string;
    bucket: string;
    key: string;
    secret: string;
  };
};

export type S3LinkDatabaseResponse = Database;

export type S3UnlinkDatabaseParams = DeleteDatabaseParams;

export type S3UnlinkDatabaseResponse = Database;

export enum DATABASES {
  MYSQL = "mysql",
  MARIADB = "mariadb",
  POSTGRESQL = "postgresql",
  MONGODB = "mongodb",
  TIMESCALEDB = "timescaledb",
  REDIS = "redis",
  UNDEFINED = ""
}

export enum DATABASE_STATUSES {
  CREATED = "CREATED",
  STOPPED = "STOPPED",
  ACTIVE = "ACTIVE",
  PROVISIONING = "PROVISIONING",
  DELETING = "DELETING",
  UPGRADING = "UPGRADING",
  DEGRADED = "DEGRADED",
  FAILED = "FAILED",
  UNKNOWN = "UNKNOWN",
  UNDEFINED = ""
}

export enum DATABASE_REPLICA_STATUSES {
  STANDALONE = "Standalone",
  PRIMARY = "Primary",
  REPLICA = "Replica",
  UNDEFINED = ""
}

export enum DATABASE_RESTORE_STATUSES {
  IN_PROGRESS = "inprogress",
  EXECUTING = "executing",
  DOWNLOADING = "downloading",
  COMPLETED = "completed"
}

// Preparing takes the pre-restore copy; RolledBack: the restore failed and the copy was
// put back; Failed: it could not be, and restore_message names the objects.
export enum DATABASE_RESTORE_PHASES {
  PREPARING = "Preparing",
  RESTORING = "Restoring",
  ROLLING_BACK = "RollingBack",
  SUCCEEDED = "Succeeded",
  ROLLED_BACK = "RolledBack",
  FAILED = "Failed"
}

export enum DATABASE_BACKUP_STATUSES {
  IN_PROGRESS = "inprogress",
  BACKING_UP = "backuping",
  UPLOADING = "uploading",
  COMPLETED = "completed"
}

export type DatabaseChild = {
  name: string;
  charset: string;
  collation: string;
  extensions?: string[];
  rejected_extensions?: Record<string, string>;
  // Extension name -> why it is not installed yet; unlike a rejection, it will be.
  pending_extensions?: Record<string, string>;
  // What the server reports installed, listed or not; absent when it reported none.
  installed_extensions?: string[];
  settings?: Record<string, string>;
  rejected_settings?: Record<string, string>;
  // Settings keys the operator has neither applied nor rejected yet.
  pending_settings?: string[];
  rejected_encoding?: string;
};

export type TableDatabaseChild = DatabaseChild & {
  id: string;
  extensionsString: string;
  settingsString: string;
  notes: string;
};

export type DatabaseUser = {
  name: string;
  permissions: DatabasePermission[];
};

export type TableDatabaseUser = {
  id: string;
  name: string;
  permissions?: string | null;
};

export type DatabasePermission = {
  database: string;
  permission: string;
};

export type GetDatabaseChildrenParams = GetDatabaseParams;
export type GetDatabaseChildrenResponse = DatabaseChild[];

export type GetDatabaseUsersParams = GetDatabaseParams;
export type GetDatabaseUsersResponse = DatabaseUser[];

export type CreateDatabaseChildParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: {
    name: string;
    charset: string;
    collation: string;
    extensions?: string[];
    settings?: Record<string, string>;
  };
};

export type UpdateDatabaseChildParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  dbName: string;
  extensions?: string[];
  settings?: Record<string, string>;
};

export type CreateDatabaseUserParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  data: DatabaseUser;
};

export type DeleteDatabaseChildParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  dbName: string;
};
export type DeleteDatabaseChildResponse = void;

export type DeleteDatabaseUserParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  username: string;
};
export type DeleteDatabaseUserResponse = void;

export type SetDatabaseUserPermissionsParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  username: string;
  permissions: DatabasePermission[];
};
export type SetDatabaseUserPermissionsResponse = void;

export type ResetDatabaseUserPasswordParams = {
  serviceName: string;
  orgId: string;
  projId: string;
  id: string;
  username: string;
};

export type DatabaseEncodings = {
  charsets: string[];
  collations: string[];
  default_charset?: string;
  default_collation?: string;
};

export type GetDatabaseEncodingsParams = GetDatabaseParams;
export type GetDatabaseEncodingsResponse = DatabaseEncodings;

export type ResetDatabaseUserPasswordResponse = DatabaseResetPassword;

// This picker is isolated from the object-storage screens and their polling.
export type BackupStorageContext = {
  regionId: string;
  projId: string;
  sessionId: string;
};
export type BackupStorageKeyContext = BackupStorageContext & {
  credsId: string;
};
export type BackupBucketCreateParams = BackupStorageKeyContext & {
  name: string;
};
export type BackupStoragePicker = {
  context: BackupStorageContext | null;
  credentialId: string;
  credentials: Credentials[] | null;
  details: Credentials | null;
  buckets: S3Bucket[] | null;
  createdCredentials: Credentials | null;
  createdBucketName: string | null;
  loadingKeys: boolean;
  loadingDetails: boolean;
  loadingBuckets: boolean;
  creatingKey: boolean;
  creatingBucket: boolean;
  errors: {
    keys: string | null;
    details: string | null;
    buckets: string | null;
    createKey: string | null;
    createBucket: string | null;
  };
};
